import { m } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
// @mui
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Badge from "@mui/material/Badge";
import Drawer from "@mui/material/Drawer";
// import Button from '@mui/material/Button';
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import { observer } from "mobx-react";
import Typography from "@mui/material/Typography";
// hooks
import { useBoolean } from "@/hooks/use-boolean";
import { useMarkMessageAsRead } from "@/hooks/useMarkMessageAsRead";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useResponsive } from "@/hooks/use-responsive";
import { useGetAllDynamoClimbsAcrossClasses } from "../../../hooks/useGetAllDynamoClimbsAcrossClasses";
import { useCareerClimbsContent } from "../../../hooks/useCMSData";
import { useGetMySections } from "@/hooks/useGetMySections";
import {
  useGetMessagesForSections,
  useGetMessagesForUserId,
} from "@/hooks/useGetMessagesForSections";
import { useMarkAllMessagesAsRead } from "@/hooks/useMarkAllMessagesAsRead";

// components
import Iconify from "@/components/iconify";
import Scrollbar from "@/components/scrollbar";
import { varHover } from "@/components/animate";
//
import NotificationItem from "./notification-item";

const NotificationsPopover = observer(() => {
  const drawer = useBoolean();
  const currentUser = useCurrentUser();

  const smUp = useResponsive("up", "sm");
  const { mutateAsync: markMessageAsRead } = useMarkMessageAsRead();
  const { mutateAsync: markAllMessagesAsRead } = useMarkAllMessagesAsRead();
  const { sections, isLoading: sectionsLoading } = useGetMySections();

  const { data: userMessages } = useGetMessagesForUserId();

  const { messages, refetch } = useGetMessagesForSections(
    sections,
    currentUser.role,
    !sectionsLoading,
  );

  const notifications = [...(userMessages ?? []), ...(messages ?? [])].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
  );

  const [totalUnRead, setTotalUnRead] = useState(0);
  const navigate = useNavigate();
  const { careerClimbs } = useCareerClimbsContent();
  const { data: dynamoCareerClimbs } = useGetAllDynamoClimbsAcrossClasses(
    currentUser.shardID,
    sections,
  );
  const mergedCareerClimbs = useMemo(() => {
    if (!careerClimbs || !dynamoCareerClimbs) return [];

    return careerClimbs.map((careerClimb) => {
      const matchingDynamoClimb = dynamoCareerClimbs.find(
        (dynamoClimb) =>
          dynamoClimb.learnosityTag === careerClimb.learnosityTag,
      );

      return matchingDynamoClimb
        ? { ...careerClimb, ...matchingDynamoClimb }
        : careerClimb;
    });
  }, [careerClimbs, dynamoCareerClimbs]);

  useEffect(() => {
    if (!notifications || !currentUser?.userID) return;

    const unreadCount = notifications.filter(
      (item) => !item.readBy?.includes(currentUser.userID),
    ).length;

    setTotalUnRead(unreadCount);
  }, [notifications, currentUser?.userID]);

  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter(
      (item) => !item.readBy.includes(currentUser.userID),
    );

    if (!unreadNotifications.length) {
      console.warn("No unread messages to mark as read.");
      return;
    }

    try {
      await markAllMessagesAsRead({
        messages: unreadNotifications,
        userID: currentUser.userID,
      });
    } catch (err) {
      console.error("Error marking all messages as read:", err);
    }
  };

  const markAsReadAndNavigate = async (notification) => {
    try {
      await markMessageAsRead({
        messageID: notification.id,
        userID: currentUser.userID,
        path: notification.path,
        timestamp: notification.timestamp,
      });

      if (notification.type === "careerClimbAssigned") {
        const assignmentDetails = careerClimbs.find(
          (climb) => climb.learnosityTag === notification.learnosityTag,
        );
        if (assignmentDetails) {
          navigate(`/career-climbs/${assignmentDetails.id}`);
        } else if (notification.path && notification.path.includes("#")) {
          // Check if this is a course plan message by checking content
          const isCoursePlanMessage =
            notification.content?.includes("Course Plan") ||
            notification.content?.includes("course plan");

          if (isCoursePlanMessage) {
            // This is a course plan notification (path format: userId#coursePlanId)
            const [climberId, coursePlanId] = notification.path.split("#");

            // Route based on user role
            if (currentUser.isEducatorRole) {
              navigate(`/teacher/course-plan/${climberId}/${coursePlanId}`);
            } else {
              navigate(`/ms/planner/course/edit/${coursePlanId}`);
            }
          } else if (currentUser.isEducatorRole) {
            // Career plan notification (path format: userId#careerPlanId).
            // Climbers no longer have a career plan page to land on, so only
            // educators are routed; the historical plan stays readable to them.
            const [climberId, careerPlanId] = notification.path.split("#");
            navigate(`/teacher/career-plan/${climberId}/${careerPlanId}`);
          }
        }
      } else if (notification.type === "careerPlanSubmitted") {
        // Student submitted a career plan
        if (notification.careerPlanId && notification.sender?.userID) {
          // Teacher view
          navigate(
            `/teacher/career-plan/${notification.sender.userID}/${notification.careerPlanId}`,
          );
        }
      } else if (notification.type === "coursePlanSubmitted") {
        // Student submitted a course plan
        const climberId = notification.sender?.userID;

        if (notification.path && notification.path.includes("#")) {
          // Path format: userId#coursePlanId#msg-xxx (message resolver appends messageId)
          const pathParts = notification.path.split("#");

          // Check if we have at least 2 parts (userId and coursePlanId)
          // The message ID is appended as a third part, so we ignore it
          if (pathParts.length >= 2) {
            const pathClimberId = pathParts[0];
            const coursePlanId = pathParts[1];

            // Verify it looks like a course plan ID
            if (coursePlanId && coursePlanId.startsWith("courseplan-")) {
              // Route based on user role
              if (currentUser.isEducatorRole) {
                navigate(
                  `/teacher/course-plan/${pathClimberId}/${coursePlanId}`,
                );
              } else if (climberId === currentUser.userID) {
                // Climber viewing their own submission
                navigate(`/ms/planner/course/edit/${coursePlanId}`);
              }
            } else if (climberId) {
              // Path doesn't have course plan format, but we have climber ID
              if (currentUser.isEducatorRole) {
                // Navigate to course planner - teachers can find the student's plan there
                navigate(`/staff/career-course-planner?tab=course`);
              } else if (climberId === currentUser.userID) {
                // Climber viewing their own submission - navigate to planner
                navigate(`/ms/planner`);
              }
            }
          } else if (climberId) {
            // Path format is unexpected
            if (currentUser.isEducatorRole) {
              navigate(`/staff/career-course-planner?tab=course`);
            } else if (climberId === currentUser.userID) {
              navigate(`/ms/planner`);
            }
          }
        } else if (climberId) {
          // No path or path doesn't match expected format, but we have climber ID
          if (currentUser.isEducatorRole) {
            // Navigate to course planner - teachers can find the student's plan there
            navigate(`/staff/career-course-planner?tab=course`);
          } else if (climberId === currentUser.userID) {
            // Climber viewing their own submission - navigate to planner
            navigate(`/ms/planner`);
          }
        }
      } else if (
        notification.type === "climbPlanSubmitted" ||
        notification.type === "climbPlanReviewed"
      ) {
        /**
         * Climb plan, both directions, one branch.
         *
         * `path` is overloaded with `<climberId>#<planId>` by both senders, and
         * the message resolver appends `#msg-xxx` — so take the first two
         * parts and ignore the rest. Who is reading decides where they land:
         * an educator gets the review page, the climber gets their own plan.
         * No prefix check like the course plan's: a climb plan's id is a bare
         * uuid, so there is nothing to match on.
         */
        const [pathClimberId, climbPlanId] = (notification.path || "").split(
          "#",
        );

        if (pathClimberId && climbPlanId) {
          if (currentUser.isEducatorRole) {
            navigate(`/teacher/climb-plan/${pathClimberId}/${climbPlanId}`);
          } else if (pathClimberId === currentUser.userID) {
            navigate(`/ms/planner/climb/edit/${climbPlanId}/review`);
          }
        } else if (currentUser.isEducatorRole) {
          navigate(`/staff/career-course-planner?tab=climb`);
        } else {
          navigate(`/ms/planner`);
        }
      } else if (notification.type === "careerClimbCompleted") {
        // Sent to coach channels when a climber finishes a career climb; the
        // sender is the climber. /users/climber is the profile route every
        // coach-facing surface links to (no level gate, unlike /climbers).
        if (currentUser.isEducatorRole && notification.sender?.userID) {
          navigate(`/users/climber/${notification.sender.userID}`);
        }
      } else {
        // Unknown type: mark it read and close, rather than guessing a route.
        // The old catch-all sent educators to the sender's climber profile,
        // which is wrong for any future type whose sender isn't a climber.
        console.warn("No navigation for notification type", notification.type);
      }

      drawer.onFalse();
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Close">
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Tooltip>

      {!smUp && (
        <IconButton onClick={drawer.onFalse}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      )}
    </Stack>
  );

  const renderList = (
    <Scrollbar>
      <List disablePadding>
        {notifications.map((notification) => {
          return (
            <NotificationItem
              key={notification.id}
              notification={notification}
              markAsRead={markAsReadAndNavigate}
              careerClimbs={mergedCareerClimbs}
              sections={sections}
            />
          );
        })}
      </List>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        color={drawer.value ? "primary" : "default"}
        onClick={() => {
          drawer.onTrue();
          refetch();
        }}
      >
        <Badge badgeContent={totalUnRead} color="error">
          <Iconify
            id="notifications-icon"
            icon="solar:bell-bing-bold-duotone"
            width={24}
          />
        </Badge>
      </IconButton>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
        }}
        PaperProps={{
          sx: { width: 1, maxWidth: 420 },
        }}
      >
        {renderHead}

        <Divider />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ pl: 2.5, pr: 1 }}
        ></Stack>

        <Divider />

        {renderList}
      </Drawer>
    </>
  );
});

export default NotificationsPopover;
